import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
  MismatchDirection,
} from 'lightweight-charts';
import type { MarkerSide } from '../../utils/vestSpikes';
import { glyphOf, isEmphasised, markerAlpha, markerScale, type CandleSpikeMarker } from '../../utils/volumeAgreement';
import { BADGE_STYLE, BADGE_STYLE_BOTH, BADGE_STYLE_WICK_STRONG, badgeBox, drawEmojiBadge, emojiScale, EMOJI_FONT_STACK, type BadgeStyle } from './emojiBadge';
import {
  LAYOUT_PRIORITY,
  MarkerLayoutClient,
  type LayoutContext,
  type LayoutItem,
  type MarkerLayoutRegistry,
} from './markerLayout';

/**
 * Volume-spike markers on candles (utils/volumeAgreement.ts): 🐋 Vest,
 * 🦈 CME, 🐋🦈 both agree (larger, cyan rim), 🐱 Vest vol ≥ X% of CME, 🪝 wick
 * rejection at the wick tip (half opacity until confirmed; strong = larger,
 * amber rim; broken = half size, quarter opacity, plain rim). Each sits on a small dark pill
 * at full opacity (see emojiBadge.ts), a little smaller on phone-width
 * charts. Drawn over the
 * DISPLAYED candle's high (net buying / no split) or under its low (net
 * selling).
 *
 * Other candle markers (⚡ signals, 🔥 volume emoji, BOS/CHoCH arrows, ABS
 * dots, ...) also stack outward from the same extreme; `occupied(time, side)`
 * says how many px they take, and these go beyond them. Two of ours on one
 * candle side (🐋 and 🦈 that did not agree) stack too.
 */

export type CandleSpikeItem = CandleSpikeMarker;

interface Placed {
  id: string;
  x: number;
  y: number;
  side: MarkerSide;
  glyph: string;
  /** Larger pill (🐋🦈, strong 🪝). */
  both: boolean;
  /** Font px before the phone-width scale (smaller for a broken 🪝). */
  font: number;
  style: BadgeStyle;
  alpha: number;
}

function styleOf(it: CandleSpikeItem): BadgeStyle {
  if (it.kind === 'both') return BADGE_STYLE_BOTH;
  if (it.kind === 'wick' && isEmphasised(it)) return BADGE_STYLE_WICK_STRONG;
  return BADGE_STYLE;
}

function layoutLabel(it: CandleSpikeItem, glyph: string): string {
  if (it.kind === 'cat') return `${glyph} Vest vol vs CME`;
  if (it.kind === 'wick') {
    const broken = it.wick?.state === 'invalidated';
    return `${glyph} ${broken ? 'Broken w' : isEmphasised(it) ? 'Strong w' : 'W'}ick rejection`;
  }
  return `${glyph} ${it.kind === 'both' ? 'Vest + CME' : it.kind === 'vest' ? 'Vest' : 'CME'} volume spike`;
}

/** Unscaled font px: 🐋🦈 / strong 🪝 larger, broken 🪝 smaller (never larger than base). */
export function fontOf(it: CandleSpikeItem): number {
  return (isEmphasised(it) ? FONT_BOTH_PX : FONT_PX) * markerScale(it);
}

function placedOf(it: CandleSpikeItem, x: number, y: number): Placed {
  return { id: it.id, x, y, side: it.side, glyph: glyphOf(it), both: isEmphasised(it), font: fontOf(it), style: styleOf(it), alpha: markerAlpha(it) };
}

/** Height one of our markers takes in a stack, px. */
export const SPIKE_STACK = 25;
export const FONT_BOTH_PX = 19;

/** Gap between the candle extreme (or the last other marker) and the whale, px. */
export const VEST_SPIKE_GAP = 4;
/** Base offset off the candle when nothing else is there, px. */
export const VEST_SPIKE_OFFSET = 16;
export const FONT_PX = 17;
const HIT_PX = 13;
/** Shared layout: px between the candle extreme and the pill. */
const LAYOUT_GAP = 4;

/** Y of the whale's centre for a candle extreme at `anchorY`. */
export function spikeY(anchorY: number, side: MarkerSide, occupiedPx: number): number {
  const off = VEST_SPIKE_OFFSET + (occupiedPx > 0 ? occupiedPx + VEST_SPIKE_GAP : 0);
  return side === 'above' ? anchorY - off : anchorY + off;
}

class Renderer implements IPrimitivePaneRenderer {
  private _items: Placed[] = [];
  update(items: Placed[]): void {
    this._items = items;
  }
  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const hr = scope.horizontalPixelRatio as number;
      const vr = scope.verticalPixelRatio as number;
      ctx.save();
      const k = emojiScale(scope.mediaSize?.width ?? 1000);
      for (const p of this._items) {
        const px = p.font * k;
        drawEmojiBadge(ctx, p.glyph, p.x, p.y, px, hr, vr, p.style, p.alpha);
      }
      ctx.restore();
    });
  }
}

class PaneView implements IPrimitivePaneView {
  private _renderer = new Renderer();
  constructor(private _source: CandleSpikePrimitive) {}
  zOrder(): PrimitivePaneViewZOrder {
    return 'top';
  }
  renderer(): IPrimitivePaneRenderer {
    this._renderer.update(this._source.place());
    return this._renderer;
  }
}

export class CandleSpikePrimitive implements ISeriesPrimitive<Time> {
  private _items: CandleSpikeItem[] = [];
  private _placed: Placed[] = [];
  private _view = new PaneView(this);
  private _requestUpdate?: () => void;
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _occupied: (timeSec: number, side: MarkerSide) => number = () => 0;
  private _onPlaced?: (placed: { id: string; x: number; y: number }[]) => void;
  private _layout = new MarkerLayoutClient('candleSpike', (ctx) => this._layoutItems(ctx));

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
    this._layout.setAttached(true);
  }

  detached(): void {
    this._layout.setAttached(false);
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  /** Join the chart's shared marker layout (null: stack off `occupied` alone). */
  setMarkerLayout(reg: MarkerLayoutRegistry | null): void {
    this._layout.setRegistry(reg);
  }

  /** One pill per marker, off the displayed candle's extreme. */
  private _layoutItems(ctx: LayoutContext): LayoutItem[] {
    const out: LayoutItem[] = [];
    const k = emojiScale(ctx.paneWidth || 1000);
    for (const it of this._items) {
      if (it.time < ctx.fromTime || it.time > ctx.toTime) continue;
      const x = ctx.timeToX(it.time);
      const bar = ctx.bar(it.time);
      if (x === null || !bar) continue;
      const anchor = ctx.priceToY(it.side === 'above' ? bar.high : bar.low);
      if (anchor === null) continue;
      const both = isEmphasised(it);
      const font = fontOf(it) * k;
      const glyph = glyphOf(it);
      const box = badgeBox(font, ctx.measure(glyph, `${Math.round(font)}px ${EMOJI_FONT_STACK}`));
      out.push({
        key: it.id,
        time: it.time,
        x,
        side: it.side,
        anchorY: anchor,
        w: box.w + 2,
        h: box.h + 2,
        gap: LAYOUT_GAP,
        priority: LAYOUT_PRIORITY.candleSpike,
        color: both ? styleOf(it).rim : 'rgba(248, 250, 252, 0.8)',
        label: layoutLabel(it, glyph),
        ref: it,
      });
    }
    return out;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._view];
  }

  updateAllViews(): void {
    /* recomputed on every render */
  }

  setItems(items: CandleSpikeItem[]): void {
    this._items = items;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  items(): CandleSpikeItem[] {
    return this._items;
  }

  setOccupied(fn: (timeSec: number, side: MarkerSide) => number): void {
    this._occupied = fn;
    this._requestUpdate?.();
  }

  setOnPlaced(cb: ((placed: { id: string; x: number; y: number }[]) => void) | undefined): void {
    this._onPlaced = cb;
  }

  place(): Placed[] {
    const out: Placed[] = [];
    if (!this._series || !this._chart) {
      this._placed = out;
      return out;
    }
    const shared = this._layout.placed();
    if (shared) {
      for (const p of shared) {
        const it = p.item.ref as CandleSpikeItem;
        out.push(placedOf(it, p.item.x, p.top + p.h / 2));
      }
      this._placed = out;
      this._onPlaced?.(out.map((p) => ({ id: p.id, x: p.x, y: p.y })));
      return out;
    }
    const ts = this._chart.timeScale();
    const ours = new Map<string, number>();
    for (const it of this._items) {
      const x = ts.timeToCoordinate(it.time as Time);
      if (x === null) continue;
      const idx = ts.timeToIndex(it.time as Time, false);
      if (idx === null) continue;
      const bar = this._series.dataByIndex(idx as unknown as number, MismatchDirection.None) as
        | { time?: unknown; high?: number; low?: number }
        | null;
      if (!bar || typeof bar.high !== 'number' || typeof bar.low !== 'number' || bar.time !== it.time) continue;
      const anchor = this._series.priceToCoordinate(it.side === 'above' ? bar.high : bar.low);
      if (anchor === null) continue;
      const key = `${it.time}:${it.side}`;
      const n = ours.get(key) ?? 0;
      ours.set(key, n + 1);
      const occupied = this._occupied(it.time, it.side) + n * SPIKE_STACK;
      out.push(placedOf(it, x, spikeY(anchor, it.side, occupied)));
    }
    this._placed = out;
    this._onPlaced?.(out.map((p) => ({ id: p.id, x: p.x, y: p.y })));
    return out;
  }

  markerAt(x: number, y: number): CandleSpikeItem | null {
    let best: Placed | null = null;
    let bestD = Infinity;
    for (const p of this._placed) {
      const d = Math.hypot((x - p.x) / (p.both ? 2 : 1), y - p.y);
      if (d <= HIT_PX && d < bestD) {
        best = p;
        bestD = d;
      }
    }
    return best ? this._items.find((i) => i.id === best!.id) ?? null : null;
  }
}
